import React from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import useTheme from 'hooks/useTheme';
import AntButton from 'components/AntButton/AntButton';
import useCoreApi from 'core/hooks/useCoreApi';
import { navigateToInventoryValidationOrders } from 'core/pricingSignalsInventoryValidationNavigation';
import { FilesDragAndDrop, Modal } from 'components';

const getPricingSignalsThemeTokens = theme => ({
	background: theme._primaryBackgroundColor,
	surface: theme._secondaryBackgroundColor,
	elevatedSurface: theme._surfaceBackgroundColor,
	textPrimary: theme._highEmphasisColor,
	textSecondary: theme._mediumEmphasisColor,
	textInactive: theme._inactiveColor,
	border: theme._borderColor,
	accent: theme._secondaryColor,
	warning: theme._warningColor,
});

const ModalContent = styled.div`
	--pricing-signals-background: ${({ $tokens }) => $tokens.background};
	--pricing-signals-surface: ${({ $tokens }) => $tokens.surface};
	--pricing-signals-surface-elevated: ${({ $tokens }) =>
		$tokens.elevatedSurface};
	--pricing-signals-text: ${({ $tokens }) => $tokens.textPrimary};
	--pricing-signals-text-secondary: ${({ $tokens }) => $tokens.textSecondary};
	--pricing-signals-text-inactive: ${({ $tokens }) => $tokens.textInactive};
	--pricing-signals-border: ${({ $tokens }) => $tokens.border};
	--pricing-signals-accent: ${({ $tokens }) => $tokens.accent};
	--pricing-signals-warning: ${({ $tokens }) => $tokens.warning};

	background: var(--pricing-signals-background);
	box-sizing: border-box;
	color: var(--pricing-signals-text);
	display: flex;
	flex-direction: column;
	gap: 20px;
	max-height: min(72vh, 680px);
	overflow-y: auto;
	padding: 24px;

	@media (max-width: 560px) {
		gap: 16px;
		padding: 20px 16px;
	}
`;

const ContextCard = styled.section`
	background: var(--pricing-signals-surface);
	border: 1px solid var(--pricing-signals-border);
	border-left: 4px solid var(--pricing-signals-accent);
	border-radius: 8px;
	padding: 14px 16px;
`;

const UploadCard = styled.section`
	background: var(--pricing-signals-surface-elevated);
	border: 1px solid var(--pricing-signals-border);
	border-radius: 8px;
	padding: 16px;
`;

const Section = styled.section`
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const SectionHeading = styled.h3`
	color: var(--pricing-signals-text);
	font-size: 15px;
	font-weight: 700;
	line-height: 1.4;
	margin: 0;
`;

const SupportingText = styled.p`
	color: var(--pricing-signals-text-secondary);
	font-size: 13px;
	line-height: 1.55;
	margin: 5px 0 0;
`;

const SectionHeader = styled.div`
	align-items: center;
	display: flex;
	gap: 10px;
	justify-content: space-between;
`;

const Badge = styled.span`
	border: 1px solid var(--pricing-signals-border);
	border-radius: 999px;
	color: var(--pricing-signals-text-secondary);
	font-size: 11px;
	font-weight: 700;
	line-height: 1;
	padding: 5px 8px;
	text-transform: uppercase;
`;

const FileSummary = styled.div`
	align-items: center;
	background: var(--pricing-signals-surface);
	border: 1px solid var(--pricing-signals-border);
	border-radius: 8px;
	display: flex;
	justify-content: space-between;
	gap: 12px;
	padding: 12px 14px;
`;

const FileSummaryText = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
`;

const FileName = styled.span`
	color: var(--pricing-signals-text);
	font-size: 14px;
	font-weight: 700;
	line-height: 1.4;
	word-break: break-word;
`;

const FileHint = styled.span`
	color: var(--pricing-signals-text-secondary);
	font-size: 12px;
	line-height: 1.45;
`;

const MetricsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 12px;

	@media (max-width: 560px) {
		grid-template-columns: 1fr;
	}
`;

const MetricCard = styled.div`
	background: var(--pricing-signals-surface-elevated);
	border: 1px solid var(--pricing-signals-border);
	border-radius: 8px;
	padding: 14px 16px;
`;

const MetricLabel = styled.div`
	color: var(--pricing-signals-text-secondary);
	font-size: 12px;
	font-weight: 700;
	line-height: 1.4;
	text-transform: uppercase;
`;

const MetricValue = styled.div`
	margin-top: 8px;
	color: var(--pricing-signals-text);
	font-size: 24px;
	font-weight: 800;
	line-height: 1;
`;

const UnmatchedCard = styled.div`
	background: var(--pricing-signals-surface-elevated);
	border: 1px solid var(--pricing-signals-border);
	border-radius: 8px;
	padding: 14px 16px;
`;

const InventoryValidationCard = styled(UploadCard)`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const InventoryValidationActions = styled.div`
	display: flex;
	justify-content: flex-end;
`;

const UnmatchedList = styled.ul`
	margin: 10px 0 0;
	padding-left: 18px;

	li {
		color: var(--pricing-signals-text-secondary);
		font-size: 12px;
		line-height: 1.55;
	}
`;

const ActionFooter = styled.div`
	align-items: center;
	background: var(--pricing-signals-background);
	border-top: 1px solid var(--pricing-signals-border);
	display: flex;
	gap: 10px;
	justify-content: flex-end;
	margin: 0 -24px -24px;
	padding: 16px 24px 20px;
	position: sticky;
	bottom: -24px;

	@media (max-width: 560px) {
		margin: 0 -16px -20px;
		padding: 14px 16px 16px;

		button {
			min-height: 40px;
		}
	}
`;

const ModalActionButton = styled(AntButton)`
	&& {
		background: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--pricing-signals-accent)'
				: 'var(--pricing-signals-surface)'};
		border-color: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--pricing-signals-accent)'
				: 'var(--pricing-signals-border)'};
		border-radius: 8px;
		box-shadow: none;
		color: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--pricing-signals-background)'
				: 'var(--pricing-signals-text)'};
		font-weight: 700;
		min-height: 40px;
		min-width: 108px;
		padding: 0 18px;
		transition:
			background-color 120ms ease,
			border-color 120ms ease,
			box-shadow 120ms ease,
			color 120ms ease;
	}

	&&:hover,
	&&:focus {
		background: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--pricing-signals-accent)'
				: 'var(--pricing-signals-surface-elevated)'};
		border-color: var(--pricing-signals-accent);
		box-shadow: 0 0 0 2px var(--pricing-signals-border);
		color: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--pricing-signals-background)'
				: 'var(--pricing-signals-accent)'};
	}

	&&[disabled],
	&&[disabled]:hover,
	&&[disabled]:focus {
		background: var(--pricing-signals-surface-elevated);
		border-color: var(--pricing-signals-border);
		box-shadow: none;
		color: var(--pricing-signals-text-inactive);
		cursor: not-allowed;
		opacity: 1;
	}

	&&.ant-btn-loading,
	&&.ant-btn-loading[disabled] {
		background: var(--pricing-signals-accent);
		border-color: var(--pricing-signals-accent);
		color: var(--pricing-signals-background);
		opacity: 0.82;
	}
`;

const EMPTY_PROCESSING = { status: undefined, message: undefined };

const getErrorMessage = error => {
	if (Array.isArray(error?.errors) && error.errors[0]?.message) {
		return error.errors[0].message;
	}
	if (Array.isArray(error) && error[0]?.message) return error[0].message;
	return error?.message || 'No fue posible importar las señales GPON';
};

export default function PricingSignalsImportModal({
	visible,
	onClose,
	onSuccess,
	sendToast,
}) {
	const { theme } = useTheme();
	const history = useHistory();
	const themeTokens = getPricingSignalsThemeTokens(theme);
	const { importGponInstallationPricingSignals } = useCoreApi();
	const [state, setState] = React.useState({
		file: undefined,
		fileName: undefined,
		isImporting: false,
		processing: EMPTY_PROCESSING,
		result: null,
	});

	const reset = React.useCallback(() => {
		setState({
			file: undefined,
			fileName: undefined,
			isImporting: false,
			processing: EMPTY_PROCESSING,
			result: null,
		});
	}, []);

	const handleClose = React.useCallback(() => {
		if (state.isImporting) return;
		reset();
		onClose?.();
	}, [onClose, reset, state.isImporting]);

	const handleFiles = React.useCallback(files => {
		const file = files?.[0];
		if (!file) return;

		setState(current => ({
			...current,
			file,
			fileName: file.name,
			result: null,
			processing: {
				status: 'success',
				message: `Archivo listo: ${file.name}`,
			},
		}));
	}, []);

	const handleImport = React.useCallback(async () => {
		if (!state.file || state.isImporting) return;

		const formData = new FormData();
		formData.append('file', state.file);

		setState(current => ({
			...current,
			isImporting: true,
			processing: {
				status: 'loading',
				message: 'Importando señales GPON...',
			},
		}));

		try {
			const result = await importGponInstallationPricingSignals(formData);
			setState(current => ({
				...current,
				isImporting: false,
				result,
				processing: {
					status: 'success',
					message: 'Importación completada correctamente',
				},
			}));
			sendToast?.({
				type: 'success',
				message: `Se importaron ${result?.matchedRows || 0} fila(s) de señales GPON`,
			});
			onSuccess?.(result);
		} catch (error) {
			const message = getErrorMessage(error);
			setState(current => ({
				...current,
				isImporting: false,
				processing: {
					status: 'unsuccess',
					message,
				},
			}));
			sendToast?.({ type: 'warn', message });
		}
	}, [
		importGponInstallationPricingSignals,
		onSuccess,
		sendToast,
		state.file,
		state.isImporting,
	]);

	const inventoryValidationOrderIds = React.useMemo(
		() =>
			Array.isArray(state.result?.inventoryValidationOrderIds)
				? state.result.inventoryValidationOrderIds
				: [],
		[state.result],
	);

	const handleOpenInventoryValidationOrders = React.useCallback(() => {
		if (inventoryValidationOrderIds.length === 0) return;

		navigateToInventoryValidationOrders({
			history,
			orderIds: inventoryValidationOrderIds,
		});
		handleClose();
	}, [handleClose, history, inventoryValidationOrderIds]);

	return (
		<Modal
			visible={visible}
			title='Importar señales GPON'
			aria-label='Importar señales GPON'
			footer={null}
			width={720}
			destroyOnClose
			onCancel={handleClose}
			closable={!state.isImporting}
			maskClosable={!state.isImporting}
		>
			<ModalContent $tokens={themeTokens}>
				<ContextCard aria-labelledby='pricing-signals-context-title'>
					<SectionHeading id='pricing-signals-context-title'>
						Señales ETA para pricing GPON
					</SectionHeading>
					<SupportingText>
						Suba el Excel aprobado por ETA/Claro para registrar las señales de
						cable adicional sobre órdenes GPON ya existentes.
					</SupportingText>
				</ContextCard>

				<UploadCard>
					<SectionHeader>
						<SectionHeading>Archivo de señales</SectionHeading>
						<Badge>XLSX</Badge>
					</SectionHeader>
					<SupportingText>
						Use el archivo consolidado de ETA correspondiente al corte que desea
						conciliar.
					</SupportingText>
					<div style={{ marginTop: 10 }}>
						<FilesDragAndDrop
							count={1}
							fileTypes={['.xlsx']}
							processing={state.processing}
							disabled={state.isImporting}
							height='120px'
							handleChange={handleFiles}
						/>
					</div>

					{state.fileName && (
						<div style={{ marginTop: 12 }}>
							<FileSummary>
								<FileSummaryText>
									<FileName>{state.fileName}</FileName>
									<FileHint>
										Archivo listo para importar y sincronizar los paycuts
										relacionados.
									</FileHint>
								</FileSummaryText>
								<Badge>Listo</Badge>
							</FileSummary>
						</div>
					)}
				</UploadCard>

				{state.result && (
					<Section aria-labelledby='pricing-signals-result-title'>
						<SectionHeading id='pricing-signals-result-title'>
							Resultado de la importación
						</SectionHeading>
						<MetricsGrid>
							<MetricCard>
								<MetricLabel>Filas matcheadas</MetricLabel>
								<MetricValue>{state.result?.matchedRows || 0}</MetricValue>
							</MetricCard>
							<MetricCard>
								<MetricLabel>Órdenes matcheadas</MetricLabel>
								<MetricValue>{state.result?.matchedOrders || 0}</MetricValue>
							</MetricCard>
							<MetricCard>
								<MetricLabel>Paycuts sincronizados</MetricLabel>
								<MetricValue>{state.result?.syncedPaycuts || 0}</MetricValue>
							</MetricCard>
							<MetricCard>
								<MetricLabel>Enviadas a validación</MetricLabel>
								<MetricValue>
									{state.result?.ordersSentToInventoryValidation || 0}
								</MetricValue>
							</MetricCard>
						</MetricsGrid>

						{inventoryValidationOrderIds.length > 0 && (
							<InventoryValidationCard>
								<div>
									<SectionHeading>
										Órdenes enviadas a validación de inventario
									</SectionHeading>
									<SupportingText>
										Abra el módulo de órdenes con el filtro de validación de
										inventario y el subconjunto exacto generado por esta
										importación.
									</SupportingText>
								</div>
								<InventoryValidationActions>
									<ModalActionButton
										type='button'
										onClick={handleOpenInventoryValidationOrders}
									>
										Ver órdenes
									</ModalActionButton>
								</InventoryValidationActions>
							</InventoryValidationCard>
						)}

						{Array.isArray(state.result?.unmatchedOdtIds) &&
							state.result.unmatchedOdtIds.length > 0 && (
								<UnmatchedCard>
									<SectionHeading>ODT sin match</SectionHeading>
									<SupportingText>
										Estas órdenes venían en el Excel pero no se encontraron como
										instalaciones GPON dentro de Synapse.
									</SupportingText>
									<UnmatchedList>
										{state.result.unmatchedOdtIds.map(odtId => (
											<li key={odtId}>{odtId}</li>
										))}
									</UnmatchedList>
								</UnmatchedCard>
							)}
					</Section>
				)}

				<ActionFooter>
					<ModalActionButton onClick={handleClose} disabled={state.isImporting}>
						Cancelar
					</ModalActionButton>
					<ModalActionButton
						$intent='primary'
						type='primary'
						disabled={!state.file}
						loading={state.isImporting}
						onClick={handleImport}
					>
						Importar
					</ModalActionButton>
				</ActionFooter>
			</ModalContent>
		</Modal>
	);
}
